import React, { useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

/**
 * Renders one assistant answer (US 1.2).
 *
 * The advisor replies in markdown: prose for a single figure, a table for a
 * breakdown. This turns that into text, tables, and — where a table holds
 * something worth comparing — a chart and a CSV download.
 *
 * Charts are built from the table in the answer rather than from a separate
 * payload, so the picture and the figures can never disagree. If the answer
 * says 60.98%, the chart is drawn from that same cell.
 *
 * No markdown library: the advisor emits a small, known subset, and the
 * alternative is a dependency and a sanitiser for the sake of four block
 * types. Nothing here is set as HTML — every value becomes a text node.
 */

type Block =
  | { kind: "p"; text: string }
  | { kind: "h"; text: string }
  | { kind: "ul"; items: string[] }
  | { kind: "ol"; start: number; items: string[] }
  | { kind: "table"; head: string[]; rows: string[][] };

/** Number of series columns beyond which a chart stops being readable. */
const MAX_SERIES = 4;

/** Rows beyond this make a pie unreadable and a bar chart crowded. */
const MAX_CHART_ROWS = 12;

/**
 * Numeric columns that are not measures, and must not become chart series.
 *
 * A rank is an ordinal and a percentage is a different scale, but both parse as
 * numbers, so every one of them was being drawn as a bar beside the values.
 * Asked which customer swung most between July and August, the chart carried
 * three series — Rank, July-2025 Balance, August-2025 Balance — where Rank was
 * the constant 1 against balances of 790,020 and 2,849,070: an invisible sliver
 * that still took a colour, a legend entry and a line in every tooltip. The
 * Trade Payables answer did the same with "% of Total" alongside balances in
 * millions.
 */
const NOT_A_MEASURE = /^(rank|#|s\.?\s*no\.?|sr\.?\s*no\.?|position|index)$/i;
const IS_A_SHARE = /%|percent|share|proportion/i;

const COLOURS = [
  "#1f5eff",
  "#0f7b46",
  "#9a6100",
  "#a62828",
  "#6b3fa0",
  "#0f6f7b",
  "#a05a00",
  "#3f5f8f",
];

// Every dash a figure can arrive with. The assistant writes a real minus
// sign, U+2212, not the ASCII hyphen, so a credit exported as
// "−₹38,92,968.00" failed to parse and the whole cell went out as quoted
// text with the symbol and separators still on it. Reported by the client as
// positive figures exporting cleanly and credit figures not.
const MINUS_LIKE = /[‐‑‒–—―−－]/g;

// What may sit in front of the digits. It was only ever the rupee sign,
// which was right until Company Config could set a base currency: a company
// on dollars would have hit exactly the same fault.
//
// A currency code is three capitals and never a month. Matched without regard
// to case, "May" in "May 2025" read as a code: it was stripped and the period
// exported as the number 2025, which is the row QA found in the download.
const CURRENCY =
  /^(?:A\$|C\$|S\$|[Rr]s\.?|[₹$€£¥]|(?!(?:JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)\b)[A-Z]{3})\s*/;

/** Indian grouping, other currencies, and the parenthesised negatives an
 * accountant may type. */
const toNumber = (cell: string): number | null => {
  let text = cell.replace(MINUS_LIKE, "-").trim();

  const bracketed = /^\((.*)\)$/.exec(text);
  if (bracketed) text = "-" + bracketed[1].trim();

  // Taken off before the symbol, because the minus comes first.
  const negative = text.startsWith("-");
  if (negative) text = text.slice(1).trim();

  let cleaned = text.replace(CURRENCY, "").replace(/[,\s%]/g, "");
  // A company set to Millions has its figures written as "1.33M", which is
  // still a figure: without this its tables lost their chart and exported as
  // text.
  const millions = /^(.*\d)M$/i.exec(cleaned);
  if (millions) cleaned = millions[1];
  if (!cleaned || !/^\d*\.?\d+$/.test(cleaned)) return null;

  const value = Number(cleaned) * (millions ? 1_000_000 : 1);
  if (!Number.isFinite(value)) return null;
  return negative ? -value : value;
};

// A numbered line: "1. RC/ASV/25-26/3 — FAIRDEAL AGENCIES".
const NUMBERED = /^\s*\d+[.)]\s+/;

// Text Excel reads as a date when it opens a CSV. "April 2025" came back as
// Apr-25 and "May 2025" as a bare 2025 - QA reported the file as not showing
// the values in the chat, and for a period column it does not.
const LOOKS_LIKE_A_DATE = new RegExp(
  [
    // April 2025, May-2025, APRIL-2025, 10 April 2025, Apr 25
    "^(\\d{1,2}[\\s\\-/])?(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\\.?[\\s\\-/,]+\\d{2,4}$",
    // 10 April, 10-Apr
    "^\\d{1,2}[\\s\\-/](jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*$",
    // 01-04-2025, 2025/04/01, 04/2025, 2025-26
    "^\\d{1,4}[\\-/.]\\d{1,2}([\\-/.]\\d{1,4})?$",
  ].join("|"),
  "i",
);

const MONTH_INDEX: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6,
  jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

/**
 * A full calendar date as an unambiguous ISO string (2018-03-05), or null.
 *
 * Dates used to go out as ="05 MAR 18" text formulas, which Excel shows
 * right but which any other reader (a script, DimeAI reading its own
 * download back) sees as a formula. ISO is read as a real date everywhere and
 * means one thing in every locale. Only dates that name a day, month and year
 * qualify: "April 2025" is a period label, not a date, and stays text. A
 * numeric day/month pair where both could be the month (04/05/2025) is left
 * alone rather than guessed, because writing the wrong date is worse than
 * writing text.
 */
export const toIsoDate = (raw: string): string | null => {
  const text = raw.trim();
  const pad = (n: number) => String(n).padStart(2, "0");
  const fullYear = (y: number) => (y >= 100 ? y : y < 70 ? 2000 + y : 1900 + y);
  const valid = (y: number, m: number, d: number) => {
    const date = new Date(Date.UTC(y, m - 1, d));
    return (
      date.getUTCFullYear() === y &&
      date.getUTCMonth() === m - 1 &&
      date.getUTCDate() === d
    );
  };

  let m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (m && valid(+m[1], +m[2], +m[3])) return text;

  m = /^(\d{1,2})[\s\-/.]([A-Za-z]{3})[a-z]*\.?[\s\-/.,]+(\d{2,4})$/.exec(text);
  if (m) {
    const month = MONTH_INDEX[m[2].toLowerCase()];
    const year = fullYear(+m[3]);
    if (month && valid(year, month, +m[1])) return `${year}-${pad(month)}-${pad(+m[1])}`;
    return null;
  }

  m = /^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/.exec(text);
  if (m) {
    const a = +m[1];
    const b = +m[2];
    const year = +m[3];
    if (a > 12 && b <= 12 && valid(year, b, a)) return `${year}-${pad(b)}-${pad(a)}`;
    if (b > 12 && a <= 12 && valid(year, a, b)) return `${year}-${pad(a)}-${pad(b)}`;
  }
  return null;
};

const splitRow = (line: string): string[] =>
  line
    .trim()
    .replace(/^\||\|$/g, "")
    .split("|")
    .map((cell) => cell.trim());

/** Strips the emphasis markers the advisor uses, for text nodes. */
const plain = (text: string): string =>
  text.replace(/\*\*(.+?)\*\*/g, "$1").replace(/`(.+?)`/g, "$1");

/** Splits on bold markers so they can be rendered as elements, not HTML. */
const Rich: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
      part.startsWith("**") && part.endsWith("**") ? (
        <strong key={i}>{part.slice(2, -2)}</strong>
      ) : (
        <React.Fragment key={i}>{plain(part)}</React.Fragment>
      ),
    )}
  </>
);

const parse = (markdown: string): Block[] => {
  const lines = (markdown || "").split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    const isDivider = /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1] || "");
    if (isTableRow && isDivider) {
      const head = splitRow(line);
      i += 2;
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ kind: "table", head, rows });
      continue;
    }

    // The same, without the header divider. The model writes these when it
    // carries a table on after a line of prose:
    //
    //     Less contra items within Operating Revenue:
    //     | Discount Allowed | ₹2,94,241.98 |
    //
    // Before this branch existed such a line matched no branch at all: the
    // table one needs a divider, and the paragraph one below refuses to
    // consume a line beginning with "|". `i` stayed where it was and the
    // parser spun forever — one stored conversation locked the browser tab
    // the moment it was opened, showing a spinner and no error, which is
    // what QA reported.
    if (isTableRow) {
      const rows: string[][] = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) {
        rows.push(splitRow(lines[i]));
        i += 1;
      }
      blocks.push({ kind: "table", head: [], rows });
      continue;
    }

    if (/^#{1,6}\s/.test(line)) {
      blocks.push({ kind: "h", text: line.replace(/^#{1,6}\s/, "") });
      i += 1;
      continue;
    }

    if (/^\s*[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\s*[-*]\s+/, ""));
        i += 1;
      }
      blocks.push({ kind: "ul", items });
      continue;
    }

    // Numbered lists were read as prose, so ten reference numbers asked for
    // one per line came out run together in a single paragraph.
    if (NUMBERED.test(line)) {
      const start = parseInt(line.trim(), 10) || 1;
      const items: string[] = [];
      while (i < lines.length && NUMBERED.test(lines[i])) {
        items.push(lines[i].replace(NUMBERED, ""));
        i += 1;
      }
      blocks.push({ kind: "ol", start, items });
      continue;
    }

    // A horizontal rule is a separator in the source, not content.
    if (/^\s*(-{3,}|_{3,})\s*$/.test(line) || line.trim() === "") {
      i += 1;
      continue;
    }

    // Seeded with the current line rather than tested for it. Every branch
    // above consumes what it matches, so anything arriving here is prose —
    // and consuming it unconditionally is what guarantees the loop advances
    // on every pass, whatever a future answer contains.
    const paragraph: string[] = [lines[i]];
    i += 1;
    while (
      i < lines.length &&
      lines[i].trim() !== "" &&
      !/^\s*\|/.test(lines[i]) &&
      !/^#{1,6}\s/.test(lines[i]) &&
      !/^\s*[-*]\s+/.test(lines[i]) &&
      !NUMBERED.test(lines[i])
    ) {
      paragraph.push(lines[i]);
      i += 1;
    }
    blocks.push({ kind: "p", text: paragraph.join(" ") });
  }

  return blocks;
};

type Series = { key: string; index: number };

/**
 * Which columns of a table can be charted.
 *
 * The label is the first column that is not a number; the series are the
 * numeric ones. A column is numeric only if every populated cell parses —
 * one stray "n/a" means the column is a note, not a measure.
 */
const chartable = (head: string[], rows: string[][]) => {
  if (!rows.length) return null;

  const numeric = head.map((_, col) => {
    const cells = rows.map((r) => r[col] ?? "").filter((c) => c !== "");
    return cells.length > 0 && cells.every((c) => toNumber(c) !== null);
  });

  const labelCol = numeric.findIndex((isNum) => !isNum);
  if (labelCol === -1) return null;

  const numericSeries: Series[] = head
    .map((key, index) => ({ key, index }))
    .filter(({ index }) => numeric[index]);

  // Ranks and percentages are dropped only while something else remains to
  // plot. A table that is nothing but shares — "% of total by vendor" — is a
  // legitimate chart, and excluding its only column would leave no chart at
  // all rather than a cleaner one.
  const measures = numericSeries.filter(
    ({ key }) => !NOT_A_MEASURE.test(plain(key).trim()) && !IS_A_SHARE.test(key),
  );
  const series = (measures.length ? measures : numericSeries).slice(0, MAX_SERIES);

  if (!series.length || rows.length > MAX_CHART_ROWS) return null;

  const data = rows.map((row) => {
    const point: Record<string, string | number> = {
      label: plain(row[labelCol] ?? ""),
    };
    series.forEach(({ key, index }) => {
      point[key] = toNumber(row[index] ?? "") ?? 0;
    });
    return point;
  });

  return { series, data };
};

/**
 * Whether a pie is honest for this table.
 *
 * Only when there is one series, nothing is negative, and the values are
 * parts of a whole. A pie of two unrelated totals, or of anything containing
 * a negative, misleads — bar is the safe default and stays available.
 */
const pieMakesSense = (
  head: string[],
  series: Series[],
  data: Record<string, string | number>[],
): boolean => {
  if (series.length !== 1) return false;
  const values = data.map((d) => Number(d[series[0].key]));
  if (values.some((v) => v < 0)) return false;
  const looksLikeShare = /share|%|percent|proportion/i.test(
    series[0].key + head.join(" "),
  );
  return looksLikeShare || data.length <= 6;
};

/** Indian grouping, so a tooltip reads like the table it was drawn from. */
const inr = new Intl.NumberFormat("en-IN", {
  maximumFractionDigits: 2,
  minimumFractionDigits: 2,
});

/**
 * The tooltip for both charts.
 *
 * Recharts' default renders a bordered white box from whatever payload it
 * holds, which is how a hover produced an empty card, and a card clipped to
 * "Ju", beside the real one. This returns null unless there is something to
 * say, so a card with no content cannot be painted at all; and it sets its own
 * width from its own text rather than being sized by a previous measurement.
 *
 * It also formats: the default printed "May-2025 : 3741569.08" next to a table
 * showing ₹37,41,569.08 for the same cell.
 */
const ChartTooltip: React.FC<any> = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;

  const rows = payload.filter(
    (entry: any) => entry && entry.value !== null && entry.value !== undefined,
  );
  if (!rows.length) return null;

  // A pie carries its slice name on the entry; a bar takes it from the axis.
  const heading = label ?? rows[0]?.name ?? "";

  return (
    <div className="dimeai-tooltip">
      {heading ? <p className="dimeai-tooltip-label">{heading}</p> : null}
      {rows.map((entry: any, i: number) => (
        <p key={i} className="dimeai-tooltip-row">
          <span
            className="dimeai-tooltip-swatch"
            style={{ background: entry.color || entry.payload?.fill }}
          />
          {rows.length > 1 || entry.name !== heading ? (
            <span className="dimeai-tooltip-name">{entry.name}</span>
          ) : null}
          <span className="dimeai-tooltip-value">
            {typeof entry.value === "number"
              ? `₹${inr.format(entry.value)}`
              : String(entry.value)}
          </span>
        </p>
      ))}
    </div>
  );
};

export const toCsv = (head: string[], rows: string[][]): string => {
  const escape = (cell: string) => {
    const value = plain(cell);
    return /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  };

  // Figures go out as figures. The on-screen table carries a rupee sign and
  // thousands separators, and exporting that text verbatim produced a column
  // a spreadsheet reads as words - it cannot be summed without a
  // find-and-replace first. Formatting is a rendering concern; the file gets
  // the number. Percentages keep their sign, since dropping it changes what
  // the column means rather than how it looks.
  // A label Excel would turn into a date goes out as a text formula, which
  // Excel shows exactly as written.
  const label = (cell: string) => {
    const text = plain(cell).trim();
    // A real date goes out as ISO; only period labels and ambiguous
    // day/month pairs still need the text formula.
    const iso = toIsoDate(text);
    if (iso) return iso;
    return LOOKS_LIKE_A_DATE.test(text)
      ? `"=""${text.replace(/"/g, '""""')}"""`
      : escape(cell);
  };

  const cellValue = (cell: string) => {
    const text = plain(cell);
    if (text.includes("%")) return escape(cell);
    const value = toNumber(text);
    return value === null ? label(cell) : String(value);
  };

  return [
    head.map(escape).join(","),
    ...rows.map((row) => row.map(cellValue).join(",")),
  ].join("\r\n");
};

const download = (head: string[], rows: string[][]) => {
  // A BOM so Excel opens the rupee sign and Indian names correctly rather
  // than as mojibake, which is the whole point of exporting for most users.
  const blob = new Blob(["﻿" + toCsv(head, rows)], {
    type: "text/csv;charset=utf-8;",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `dimeai-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const TableBlock: React.FC<{ head: string[]; rows: string[][] }> = ({
  head,
  rows,
}) => {
  const chart = useMemo(() => chartable(head, rows), [head, rows]);
  const canPie = chart ? pieMakesSense(head, chart.series, chart.data) : false;
  const [view, setView] = useState<"table" | "bar" | "pie">("table");

  // Counted from the rows as well as the header, because a table that lost
  // its header divider has no header at all and its figures should still
  // line up on the right.
  const columns = useMemo(
    () => Math.max(head.length, ...rows.map((r) => r.length), 0),
    [head, rows],
  );
  const numericCol = useMemo(
    () =>
      Array.from({ length: columns }, (_, col) =>
        rows.every((r) => (r[col] ?? "") === "" || toNumber(r[col]) !== null),
      ),
    [columns, rows],
  );

  return (
    <div className="dimeai-output">
      <div className="dimeai-output-actions">
        {chart && (
          <div className="dimeai-view-toggle" role="group" aria-label="View as">
            {(["table", "bar", ...(canPie ? ["pie" as const] : [])] as const).map(
              (option) => (
                <button
                  key={option}
                  type="button"
                  className={
                    view === option
                      ? "dimeai-view-btn dimeai-view-btn--on"
                      : "dimeai-view-btn"
                  }
                  onClick={() => setView(option)}
                >
                  {option === "table" ? "Table" : option === "bar" ? "Bar" : "Pie"}
                </button>
              ),
            )}
          </div>
        )}
        <button
          type="button"
          className="dimeai-download-btn"
          onClick={() => download(head, rows)}
        >
          Download CSV
        </button>
      </div>

      {view === "table" || !chart ? (
        <div className="dimeai-table-scroll">
          <table className="dimeai-table">
            {/* No header row at all when the answer gave none, rather than
                an empty one ruled off above the figures. */}
            {head.length > 0 && (
              <thead>
                <tr>
                  {head.map((cell, i) => (
                    <th key={i} className={numericCol[i] ? "num" : undefined}>
                      <Rich text={cell} />
                    </th>
                  ))}
                </tr>
              </thead>
            )}
            <tbody>
              {rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td key={c} className={numericCol[c] ? "num" : undefined}>
                      <Rich text={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="dimeai-chart">
          {/* Taller than the pie needs, because the bar chart's angled labels
              claim 86px of it; a shared height keeps the two views from
              jumping in size when the user switches between them. */}
          <ResponsiveContainer width="100%" height={300}>
            {view === "bar" ? (
              <BarChart data={chart.data} margin={{ top: 8, right: 8, bottom: 8, left: 8 }}>
                {/* Vendor and ledger names are long — "SATYANARAYAN RAJKAMAL
                    SYNTHETICS (P) LTD." — and at -15° they ran into each other
                    and off the left edge of the plot. A steeper angle plus a
                    truncated tick keeps them legible; the full name is still
                    in the tooltip, so nothing is lost by shortening it here. */}
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 11 }}
                  interval={0}
                  angle={-35}
                  textAnchor="end"
                  height={86}
                  tickMargin={6}
                  tickFormatter={(value: string) =>
                    value && value.length > 16 ? `${value.slice(0, 15)}…` : value
                  }
                />
                <YAxis tick={{ fontSize: 11 }} width={72} />
                <Tooltip content={<ChartTooltip />} cursor={{ fill: "rgba(127,127,127,0.12)" }} />
                {chart.series.length > 1 && <Legend wrapperStyle={{ fontSize: 12 }} />}
                {chart.series.map(({ key }, i) => (
                  <Bar key={key} dataKey={key} fill={COLOURS[i % COLOURS.length]} />
                ))}
              </BarChart>
            ) : (
              <PieChart>
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Pie
                  data={chart.data}
                  dataKey={chart.series[0].key}
                  nameKey="label"
                  outerRadius={92}
                  label={false}
                >
                  {chart.data.map((_, i) => (
                    <Cell key={i} fill={COLOURS[i % COLOURS.length]} />
                  ))}
                </Pie>
              </PieChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const AnswerBody: React.FC<{ content: string }> = ({ content }) => {
  const blocks = useMemo(() => parse(content), [content]);

  return (
    <>
      {blocks.map((block, i) => {
        if (block.kind === "table") {
          return <TableBlock key={i} head={block.head} rows={block.rows} />;
        }
        if (block.kind === "h") {
          return (
            <h4 key={i} className="dimeai-answer-heading">
              <Rich text={block.text} />
            </h4>
          );
        }
        if (block.kind === "ol") {
          return (
            <ol key={i} className="dimeai-answer-list" start={block.start}>
              {block.items.map((item, j) => (
                <li key={j}>
                  <Rich text={item} />
                </li>
              ))}
            </ol>
          );
        }
        if (block.kind === "ul") {
          return (
            <ul key={i} className="dimeai-answer-list">
              {block.items.map((item, j) => (
                <li key={j}>
                  <Rich text={item} />
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={i} className="dimeai-answer-para">
            <Rich text={block.text} />
          </p>
        );
      })}
    </>
  );
};

export default AnswerBody;
